import { KeyRound, Webhook, Slack } from "lucide-react";
import { GeometricBadge } from "../playful/GeometricBadge";
import { FloatingIcon } from "../playful/FloatingIcon";
import { Reveal, useInView, reveal } from "./Reveal";

const TOOLS = [
  {
    icon: KeyRound,
    tone: "mint",
    title: "REST API + keys",
    desc: "Scoped API keys (links / analytics) for full programmatic access.",
    plan: "Enterprise",
  },
  {
    icon: Webhook,
    tone: "candy",
    title: "Webhooks",
    desc: "HMAC-signed events for clicks, creates, updates & deletes.",
    plan: "Pro+",
  },
  {
    icon: Slack,
    tone: "pink",
    title: "Integrations",
    desc: "Pipe link events straight into Slack & Discord channels.",
    plan: "Pro+",
  },
];

const shadow = {
  candy: "shadow-sticker-candy",
  pink: "shadow-sticker-pink",
  mint: "shadow-sticker-mint",
};

/** DeveloperSection — the developer platform with a payload snippet. */
export default function DeveloperSection() {
  const [ref, visible] = useInView(0.08);
  return (
    <section
      className="overflow-hidden border-y-2 border-ink bg-candy-tint"
      data-testid="developer-section"
    >
      <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10">
        <div ref={ref} className="flex flex-col items-center gap-10 lg:flex-row">
          {/* Copy + tools */}
          <div className="w-full min-w-0 lg:w-1/2">
            <div {...reveal(visible, 0)}>
              <GeometricBadge tone="candy">DEVELOPER PLATFORM</GeometricBadge>
              <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tight md:text-5xl">
                Automate every
                <span className="ai-gradient-text"> link in your stack.</span>
              </h2>
              <p className="mt-5 max-w-lg font-sans text-base leading-relaxed text-ink/70 md:text-lg">
                LinkLane is API-first. Create links in code, subscribe to signed webhooks, and fan
                events out to your tools — no glue scripts required.
              </p>
            </div>
            <div className="mt-7 flex flex-col gap-3">
              {TOOLS.map((t, i) => (
                <Reveal key={t.title} delay={i * 80}>
                  <div
                    className={`border-2 border-ink ${shadow[t.tone]} flex items-center gap-4 rounded-2xl bg-white p-4`}
                  >
                    <FloatingIcon icon={t.icon} tone={t.tone} size={44} shape="square" />
                    <div className="flex-1">
                      <h3 className="font-display text-base font-extrabold">{t.title}</h3>
                      <p className="font-sans text-[13px] leading-snug text-muted-foreground">
                        {t.desc}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full border-2 border-ink bg-yellow-tint px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest shadow-sticker-xs">
                      {t.plan}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Code / payload card */}
          <div className="w-full min-w-0 lg:w-1/2" {...reveal(visible, 140)}>
            <div className="overflow-hidden rounded-3xl border-2 border-ink bg-ink shadow-sticker-lg">
              <div className="flex items-center gap-2 border-b-2 border-white/15 px-4 py-3">
                <div className="flex gap-1.5">
                  <span className="h-3 w-3 rounded-full bg-pink" />
                  <span className="h-3 w-3 rounded-full bg-yellow" />
                  <span className="h-3 w-3 rounded-full bg-mint" />
                </div>
                <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-white/40">
                  POST /api/links
                </span>
              </div>
              <pre className="overflow-x-auto p-5 font-mono text-[12px] leading-relaxed text-white/85">
                {`curl -X POST https://linklane.io/api/links \\
  -H "Authorization: Bearer ll_live_••••" \\
  -d '{ "url": "https://example.com",
        "custom_alias": "launch" }'

→ 201 Created
{
  "code": "launch",
  "short_url": "https://linklane.io/launch",
  "qr": "https://linklane.io/launch.svg"
}`}
              </pre>
              <div className="flex items-center gap-2 border-t-2 border-white/15 px-5 py-3">
                <Webhook size={13} className="text-mint" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                  webhook fires: link.created
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
